import { useEffect, useId, useState } from "react";
import { Bot, BrainCircuit, ChevronDown, Clock3, History, MessageSquare } from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

export const AGENT_CHILDREN = [
  { to: "/agent/chat", label: "Chat", icon: MessageSquare },
  { to: "/agent/sessions", label: "Sessions", icon: History },
  { to: "/brain", label: "The Brain", icon: BrainCircuit },
  { to: "/agent/automations", label: "Skills & Automations", icon: Clock3 },
];

const testId = (label) => `nav-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`;

export default function AgentNavigation({ collapsed = false, mobile = false, agentName = "Your Agent" }) {
  const { pathname } = useLocation();
  const childActive = AGENT_CHILDREN.some(({ to }) => pathname === to || pathname.startsWith(`${to}/`));
  const [open, setOpen] = useState(true);
  const expanded = open || childActive;
  const childrenId = useId();

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  const rowClass = mobile
    ? "whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium"
    : `group flex min-w-0 flex-1 items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${collapsed ? "justify-center px-0" : ""}`;

  return (
    <div className={mobile ? "flex items-center gap-1" : "space-y-1"}>
      <div className="flex items-center gap-1">
        <NavLink
          to="/"
          end
          data-testid="nav-your-agent"
          title={collapsed ? agentName : undefined}
          className={({ isActive }) => `${rowClass} ${isActive
            ? mobile ? "bg-white/10 text-white" : "border-sm-accent/50 bg-white/10 text-white"
            : mobile ? "text-white/50" : "border-transparent text-white/55 hover:bg-white/5 hover:text-white"}`}
        >
          {!mobile ? <Bot size={17} className="shrink-0" aria-hidden="true" /> : null}
          {!collapsed || mobile ? <span className="truncate">{agentName}</span> : null}
        </NavLink>
        <button
          type="button"
          data-testid="agent-nav-disclosure"
          aria-label={childActive ? `${agentName} pages stay open while a page is active` : expanded ? `Hide ${agentName} pages` : `Show ${agentName} pages`}
          aria-expanded={expanded}
          aria-controls={childrenId}
          disabled={childActive}
          onClick={() => setOpen((value) => !value)}
          className={`shrink-0 rounded-md text-white/55 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sm-accent disabled:cursor-default ${mobile ? "p-1.5" : "p-2"}`}
        >
          <ChevronDown size={15} aria-hidden="true" className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      </div>
      {expanded ? (
        <div id={childrenId} className={mobile ? "flex items-center gap-1" : "space-y-1"}>
          {AGENT_CHILDREN.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testId(label)}
              title={collapsed ? label : undefined}
              className={({ isActive }) => mobile
                ? `whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium ${isActive ? "bg-white/10 text-white" : "text-white/50"}`
                : `flex items-center gap-3 rounded-lg border py-2 text-xs font-medium transition-colors ${collapsed ? "justify-center px-0" : "pl-9 pr-3"} ${isActive ? "border-sm-accent/40 bg-white/[0.09] text-white" : "border-transparent text-white/50 hover:bg-white/5 hover:text-white"}`}
            >
              <Icon size={14} className="shrink-0" aria-hidden="true" />
              {!collapsed || mobile ? <span>{label}</span> : null}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}
